import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";

import { PagedResultModel } from "../../models/paged-result.model";
import { BrowserWindowService } from "../../services/environment/browser-window.service";
import { BaseFormControlComponent } from "../base-form-control.component";

import { PaginationItemType } from "./pagination-item-type.enum";
import { PaginationItemModel } from "./pagination-item.model";
import { PaginationOptionsModel } from "./pagination-options.model";

@Component({
    selector: "pcg-ng-pagination",
    templateUrl: "./pagination.component.html",
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PaginationComponent), multi: true }]
})
export class PaginationComponent extends BaseFormControlComponent<number> implements OnInit, OnChanges {
    @Input()
    public pagination: PagedResultModel;

    @Input()
    public options: PaginationOptionsModel;

    @Input()
    public theme: string;

    @Input()
    public parentSelector: string;

    public items: Array<PaginationItemModel> = new Array<PaginationItemModel>();

    public constructor(
        private _browserWindowService: BrowserWindowService
    ) {
        super();
    }

    public ngOnInit(): void {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.options = this.options || new PaginationOptionsModel();

        const newValue = changes.pagination.currentValue as PaginationItemModel;

        if (newValue) {
            if (this.value !== newValue.pageNumber) {
                this.writeNewValue(newValue.pageNumber);
            }

            this.buildPaginationModel();
        }
    }

    public onPageNumberClicked(pageNumber: number): boolean {
        this.writeNewValue(pageNumber);
        return false;
    }

    private buildPaginationModel(): void {
        this.items = [];

        const pagination = this.pagination;

        if (pagination.totalPages <= this.options.buttonsCount) {
            for (let i = 1; i <= pagination.totalPages; i++) {
                this.addPaginationItem(i, PaginationItemType.NAVIGATION_BUTTON);
            }
        } else {
            const rangeStartIndex = Math.min(
                Math.max(0, (pagination.pageNumber - Math.ceil(this.options.buttonsCount / 2))),
                (pagination.totalPages - this.options.buttonsCount)
            );

            if (pagination.pageNumber > 1) {
                if (this.options.showGoToFirst) {
                    this.addPaginationItem(1, PaginationItemType.NAVIGATE_TO_FIRST_PAGE);
                }

                if (this.options.showGoToPrevious) {
                    this.addPaginationItem(pagination.pageNumber - 1, PaginationItemType.NAVIGATE_TO_PREVIOUS_PAGE);
                }
            }

            if ((rangeStartIndex > 0) && this.options.showSkipLabel) {
                this.addPaginationItem(1, PaginationItemType.NAVIGATION_BUTTON);

                if (rangeStartIndex > 1) {
                    this.addPaginationItem(0, PaginationItemType.SKIP_LABEL);
                }
            }

            for (let i = (rangeStartIndex + 1); i <= (rangeStartIndex + this.options.buttonsCount); i++) {
                this.addPaginationItem(i, PaginationItemType.NAVIGATION_BUTTON);
            }

            if ((rangeStartIndex < (pagination.totalPages - this.options.buttonsCount)) && this.options.showSkipLabel) {
                if (rangeStartIndex < (pagination.totalPages - this.options.buttonsCount - 1)) {
                    this.addPaginationItem(0, PaginationItemType.SKIP_LABEL);
                }

                this.addPaginationItem(pagination.totalPages, PaginationItemType.NAVIGATION_BUTTON);
            }

            if (pagination.pageNumber < pagination.totalPages) {
                if (this.options.showGoToNext) {
                    this.addPaginationItem(pagination.pageNumber + 1, PaginationItemType.NAVIGATE_TO_NEXT_PAGE);
                }

                if (this.options.showGoToLast) {
                    this.addPaginationItem(pagination.totalPages, PaginationItemType.NAVIGATE_TO_LAST_PAGE);
                }
            }
        }
    }

    private addPaginationItem(pageNumber: number, buttonType: PaginationItemType): void {
        let displayLabel: string = String("");
        let ariaLabel: string = String("");
        let buttonStyle: string = String("");

        switch (buttonType) {
            case PaginationItemType.NAVIGATION_BUTTON: {
                displayLabel = String(pageNumber);
                buttonStyle = "pcg-pagination__link";
                break;
            }
            case PaginationItemType.NAVIGATE_TO_FIRST_PAGE: {
                displayLabel = this.options.labelFirst;
                ariaLabel = "";
                buttonStyle = "";
                break;
            }
            case PaginationItemType.NAVIGATE_TO_LAST_PAGE: {
                displayLabel = this.options.labelLast;
                ariaLabel = "";
                buttonStyle = "";
                break;
            }
            case PaginationItemType.NAVIGATE_TO_PREVIOUS_PAGE: {
                displayLabel = this.options.labelPrevious;
                ariaLabel = "Previous page";
                buttonStyle = "pcg-pagination__link pcg-pagination__link--prev";
                break;
            }
            case PaginationItemType.NAVIGATE_TO_NEXT_PAGE: {
                displayLabel = this.options.labelNext;
                ariaLabel = "Next page";
                buttonStyle = "pcg-pagination__link pcg-pagination__link--next";
                break;
            }
            case PaginationItemType.SKIP_LABEL: {
                displayLabel = this.options.labelSkip;
                buttonStyle = "";
                break;
            }
        }

        const navigationUrl = (pageNumber > 0) ? this.generateNavigationUrl(pageNumber, this.options.queryKey) : "";
        const isCurrentPage = (this.value === pageNumber) && (String(pageNumber) === displayLabel);

        const paginationItem = new PaginationItemModel(pageNumber, displayLabel, ariaLabel, buttonType, buttonStyle, navigationUrl, isCurrentPage);

        this.items.push(paginationItem);
    }

    private generateNavigationUrl(pageIndex: number, queryKey: string): string {
        const currentQueryString = (this._browserWindowService.getQueryString() || "");
        const queryParams = currentQueryString.split("&");
        const updatedQueryString = queryParams
            .filter(queryParam => queryParam.split("=")[0] !== queryKey)
            .concat([`${queryKey}=${pageIndex}`])
            .join("&");

        return `?${updatedQueryString}`;
    }
}
